import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Input, DatePicker, TimePicker, Form, Pagination } from 'antd';
import {
  CalendarOutlined,
  TrophyFilled,
  FormOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import { CustomTable } from '../../common/CustomTable';
import {
  BracketDetailsData,
  RoundDto,
  TournamentMatchUpdatePayload,
} from '../../../types/types';
import { WarningFilled } from '@ant-design/icons';
import { Button } from 'antd';
import { getTournamentBracketRequest } from '../../../api/requests/tournaments';
import Spinner from '../../common/Spinner';
import MatchVenueDetailsModal from './MatchVenueDetailsModal';
import {
  buildMatchScorePath,
  buildSinglesGenerateBracketPath,
} from '../../../routes/routePaths';
import { parseScoreData } from '../../../utils/eventUtils';
import { useEventContext } from '../../../contexts/EventContext';
import CustomButton from '../../common/Button';

interface RoundDataType {
  key: number;
  sno: number;
  player1: string;
  player2: string;
  winner: string | null;
  tournamentMatchId: number;
  score1: any;
  score2: any;
  venue: string;
  date: Dayjs;
  time: Dayjs;
  facilityId: number;
}

export type ScoreData =
  | { sets?: number[] }
  | { games?: number[] }
  | null
  | undefined;

const SinglesBrackets: React.FC = () => {
  const navigate = useNavigate();
  const { sportName } = useEventContext();
  const { eventId, tournamentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const roundFromUrl = Number(searchParams.get('round')) || 1;
  const pageFromUrl = Number(searchParams.get('page')) || 1;
  const [bracketDetails, setBracketDetails] = useState<BracketDetailsData>({
    totalMatchups: 0,
    totalCompletedMatchups: 0,
    rounds: [],
  });
  const [selectedRound, setSelectedRound] = useState<number>(roundFromUrl);
  const [currentPage, setCurrentPage] = useState<number>(pageFromUrl);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [updateModalOpen, setUpdateModalOpen] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<
    TournamentMatchUpdatePayload | undefined
  >(undefined);

  const pageSize = 8;

  const fetchBracketDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getTournamentBracketRequest(Number(tournamentId));
      if (response?.isSuccess) {
        setBracketDetails(response.value);
      }
    } catch (error) {
      console.error('Error fetching bracket details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const roundsList = useMemo(() => {
    return (
      bracketDetails?.rounds?.map((r: RoundDto) => ({
        id: r.round,
        name: r.roundName,
      })) || []
    );
  }, [bracketDetails]);

  const tableData = useMemo(() => {
    if (!bracketDetails) return [];
    const round = bracketDetails.rounds.find((r) => r.round === selectedRound);
    if (!round) return [];
    return round.matchups.map((m: any, index: number) => {
      const p1Score =
        m.match?.participant1ScoreData && sportName
          ? parseScoreData(m.match.participant1ScoreData, sportName)
          : null;
      const p2Score =
        m.match?.participant2ScoreData && sportName
          ? parseScoreData(m.match.participant2ScoreData, sportName)
          : null;
      return {
        key: m.match?.tournamentMatchId ?? m.bracketId,
        sno: index + 1,
        player1: m.participant1?.participantName ?? 'TBD',
        player2: m.participant2?.participantName ?? 'TBD',
        winner: m.winner
          ? m.winner?.participantId === m.participant1?.participantId
            ? 'player1'
            : m.winner?.participantId === m.participant2?.participantId
              ? 'player2'
              : null
          : null,

        tournamentMatchId: m.match?.tournamentMatchId,
        score1: p1Score,
        score2: p2Score,
        venue: m.match?.matchLocation || '',
        date: m.match?.scheduledStartTime
          ? dayjs(m.match.scheduledStartTime)
          : dayjs(),
        time: m.match?.scheduledStartTime
          ? dayjs(m.match.scheduledStartTime)
          : dayjs(),
        facilityId: m.match?.facilityId,
      };
    });
  }, [bracketDetails, selectedRound]);

  const paginatedTableData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return tableData.slice(start, end);
  }, [tableData, currentPage]);

  const renderScore = (score?: ScoreData): string | null => {
    if (!score) return null;
    if ('sets' in score && Array.isArray(score.sets) && score.sets.length > 0) {
      return score.sets.join(' | ');
    }
    if (
      'games' in score &&
      Array.isArray(score.games) &&
      score.games.length > 0
    ) {
      return score.games.join(' | ');
    }
    return null;
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      width: 80,
    },
    {
      title: 'Players',
      render: (_: any, record: RoundDataType) => (
        <PlayerCellWrapper>
          <PlayerCell>
            {record.player1}
            {record.winner === 'player1' && <TrophyIcon />}
          </PlayerCell>
          <PlayerCell>
            {record.player2}
            {record.winner === 'player2' && <TrophyIcon />}
          </PlayerCell>
        </PlayerCellWrapper>
      ),
    },
    {
      title: 'Score',
      render: (_: any, record: RoundDataType) => (
        <PlayerCellWrapper>
          <PlayerCellWrapper>
            <ScoreCell>{renderScore(record.score1)}</ScoreCell>
            <ScoreCell>{renderScore(record.score2)}</ScoreCell>
          </PlayerCellWrapper>
        </PlayerCellWrapper>
      ),
    },
    {
      title: 'Actions',
      render: (_: any, record: RoundDataType) => (
        <Actions>
          <ActionLink
            disabled={scoringBlocked}
            onClick={() => {
              if (scoringBlocked) return;
              handleMatchScore({ id: record.key });
            }}
          >
            Score
          </ActionLink>
          <VenueUpdateIcon onClick={() => handleUpdateMatchPress(record)} />
        </Actions>
      ),
    },
  ];

  const handleUpdateMatchPress = (record: RoundDataType) => {
    setUpdateModalOpen(true);
    setInitialValues({
      facilityId: record.facilityId,
      tournamentMatchId: record.tournamentMatchId || 0,
      tournamentMatchLocation: record.venue,
      scheduledStartTime: record.date.toISOString(),
    });
  };

  const handleClose = () => {
    setUpdateModalOpen(false);
  };

  const handleMatchScore = ({ id }: { id: number }) => {
    const path = buildMatchScorePath(Number(eventId), Number(tournamentId), id);
    navigate(`${path}?round=${selectedRound}&page=${currentPage}`);
  };

  const isStale = Boolean(bracketDetails?.bracketIsStale);
  const unpairedLeftoverCount = bracketDetails?.unpairedLeftoverCount ?? 0;
  const registrationOpen = Boolean(bracketDetails?.registrationWindowOpen);
  // Scoring is blocked while registration is still open or the bracket is stale.
  const scoringBlocked = isStale || registrationOpen;

  // A match has started once any matchup is in progress or completed.
  const isAnyMatchStarted = useMemo(() => {
    return (
      bracketDetails?.rounds?.some((round) =>
        round.matchups?.some(
          (m: any) =>
            m.match?.statusName === 'InProgress' ||
            m.match?.statusName === 'Completed',
        ),
      ) ?? false
    );
  }, [bracketDetails]);

  // Regeneration is allowed any time before a match starts — to absorb new
  // registrations (stale) or simply to reshuffle / change the format.
  const canRegenerate = !isAnyMatchStarted;

  const handleRegenerate = () => {
    navigate(
      `/event-management/${eventId}/${buildSinglesGenerateBracketPath(
        Number(tournamentId),
      )}`,
    );
  };

  useEffect(() => {
    fetchBracketDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRound]);

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  return (
    <MainContainer>
      {registrationOpen && (
        <StaleBanner>
          <StaleBannerIcon />
          <StaleBannerText>
            Registration is still open for this tournament. Close registration
            before entering scores — otherwise players registering now can no
            longer be added once a match begins.
          </StaleBannerText>
        </StaleBanner>
      )}
      {isStale && (
        <StaleBanner>
          <StaleBannerIcon />
          <StaleBannerText>
            New registrations have been received and unpaired players can now be
            paired. The bracket is outdated — regenerate it to include everyone
            before starting matches or entering scores.
          </StaleBannerText>
          <Button type="primary" size="small" onClick={handleRegenerate}>
            Regenerate Bracket
          </Button>
        </StaleBanner>
      )}
      {unpairedLeftoverCount > 0 && (
        <InfoBanner>
          <InfoBannerText>
            {`${unpairedLeftoverCount} ${
              unpairedLeftoverCount === 1 ? 'player is' : 'players are'
            } unpaired and not included in the bracket.`}
          </InfoBannerText>
        </InfoBanner>
      )}

      <RoundsScrollContainer>
        <RoundsWrapper>
          {roundsList.map((round) => {
            const isActive = selectedRound === round.id;
            return (
              <RoundButton
                key={round.id}
                active={isActive}
                onClick={() => {
                  setSelectedRound(round.id);
                  setSearchParams({
                    round: round.id.toString(),
                    page: currentPage.toString(),
                  });
                }}
              >
                <RoundButtonText active={isActive}>
                  {round.name}
                </RoundButtonText>
              </RoundButton>
            );
          })}
        </RoundsWrapper>
        {!isStale && canRegenerate && (
          <CustomButton type="primary" size="small" onClick={handleRegenerate}>
            Regenerate Bracket
          </CustomButton>
        )}
      </RoundsScrollContainer>
      <TableSection>
        <BodyContainer>
          <TableScrollArea>
            <CustomTable
              dataSource={paginatedTableData}
              columns={columns}
              rowKey="key"
              pagination={false}
              loading={isLoading}
              enableInnerScroll={false}
            />
          </TableScrollArea>
          <PaginationWrapper>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={tableData.length}
              onChange={(page) => {
                setCurrentPage(page);
                setSearchParams({
                  round: selectedRound.toString(),
                  page: page.toString(),
                });
              }}
            />
          </PaginationWrapper>
        </BodyContainer>
      </TableSection>
      {updateModalOpen && (
        <MatchVenueDetailsModal
          visible={updateModalOpen}
          handleClose={handleClose}
          initialValues={initialValues}
          onUpdate={fetchBracketDetails}
        />
      )}
    </MainContainer>
  );
};

export default SinglesBrackets;

/* -------------------- STYLES -------------------- */

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  height: 100%;
  min-height: 0;
  background-color: ${COLORS.white.primary};
`;

const RoundsScrollContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  overflow-x: auto;
  padding: 8px 16px;
  background-color: ${COLORS.white.primary};
  border-bottom: 1px solid ${COLORS.grey.light};
  justify-content: space-between;
`;

const RoundsWrapper = styled.div`
  display: flex;
  gap: 10px;
  min-width: min-content;
`;

const RoundButton = styled.button<{ active: boolean }>`
  background-color: ${(props) =>
    props.active ? COLORS.blue.primary : COLORS.grey.extraLight};
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  &:hover {
    background-color: ${(props) =>
      props.active ? COLORS.black.primary : COLORS.grey.lightMedium};
  }
`;

const RoundButtonText = styled.span<{ active: boolean }>`
  color: ${(props) =>
    props.active ? COLORS.white.primary : COLORS.black.primary};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const BodyContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const TableScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const PaginationWrapper = styled.div`
  flex-shrink: 0;
  padding-top: 12px;
  display: flex;
  justify-content: flex-end;
  background: ${COLORS.white.primary};
`;

const TableSection = styled.div`
  flex: 1;
  min-height: 0;
  background: ${COLORS.white.primary};
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 10px 0;
`;

const PlayerCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PlayerCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ScoreCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${COLORS.skyBlue.primary};
`;

export const TrophyIcon = styled(TrophyFilled)`
  margin-left: 8px;
  font-size: ${TEXT_SIZES.LG}px;
  color: ${COLORS.orange.primary};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const ActionLink = styled.span<{ disabled?: boolean }>`
  color: ${COLORS.black.primary};
  border: 0.5px solid ${COLORS.grey.secondary};
  border-radius: 4px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  padding: 4px 8px;
  background: ${COLORS.grey.lightMedium};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

const StaleBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin: 12px 16px 0;
  padding: 10px 14px;
  border-radius: 6px;
  background: #fff4e5;
  border: 1px solid ${COLORS.orange.primary};
`;

const StaleBannerIcon = styled(WarningFilled)`
  color: ${COLORS.orange.primary};
  font-size: ${TEXT_SIZES.LG}px;
  flex-shrink: 0;
`;

const StaleBannerText = styled.div`
  flex: 1;
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  line-height: 1.4;
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin: 12px 16px 0;
  padding: 10px 14px;
  border-radius: 6px;
  background: ${COLORS.skyBlue.light};
  border: 1px solid ${COLORS.skyBlue.primary};
`;

const InfoBannerText = styled.div`
  flex: 1;
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  line-height: 1.4;
`;

export const StyledFormItem = styled(Form.Item)`
  margin-bottom: 18px !important;
  .ant-form-item-label > label {
    color: ${COLORS.black.primary};
    font-weight: ${FONT_WEIGHTS.MEDIUM};
    font-size: ${TEXT_SIZES.SM}px;
  }
  .ant-form-item-control {
    display: flex;
    flex-direction: column;
  }
  .ant-form-item-explain-error {
    font-size: ${TEXT_SIZES.XS}px;
    line-height: 1.3;
    margin-top: 16px;
  }
`;

export const StyledInput = styled(Input)`
  height: 43px;
  input {
    height: 43px;
  }
`;

export const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  height: 43px;
  .ant-picker-input > input {
    height: 43px;
  }
`;

export const StyledTimePicker = styled(TimePicker)`
  width: 100%;
  height: 43px;
  .ant-picker-input > input {
    height: 43px;
  }
`;

export const BlueCalendarIcon = styled(CalendarOutlined)`
  color: ${COLORS.skyBlue.primary};
  font-size: ${TEXT_SIZES.MD}px;
`;

const VenueUpdateIcon = styled(FormOutlined)`
  font-size: ${TEXT_SIZES.MD}px;
  cursor: pointer;
  z-index: 1;
`;
